import type { Cart } from './cart';
import { data, find, findAll } from './dom';
import { headerImage, miniSprite, sheetImage } from './theme-images';

export interface ThemeDetail {
  open(trigger: HTMLElement, withHeader: boolean): void;
}

interface Theme {
  readonly id: string;
  readonly name: string;
  readonly groupName: string;
  readonly text: string;
  readonly look: string;
  readonly company: string;
  readonly captions: readonly string[];
}

// Iznad 20 komada „+“ i „−“ menjaju količinu za po 5.
const largeStepFrom = 20;
const largeStep = 5;

// Detalj teme: 12 listova se lista prevlačenjem, strelicama i sličicama, izgled je sa zaglavljem primer-firme ili bez
// njega, a izabrana količina ide u porudžbinu. Podaci teme stižu iz data-* atributa dugmeta na kartici.
export function enhanceThemeDetail(cart: Cart): ThemeDetail | null {
  const dialog = document.querySelector('[data-js="theme-detail"]');
  if (!(dialog instanceof HTMLDialogElement)) return null;

  const track = find(dialog, 'theme-track', HTMLElement);
  const slides = findAll(dialog, 'theme-slide', HTMLElement);
  const pages = findAll(dialog, 'theme-page', HTMLElement);
  const minis = findAll(dialog, 'theme-mini', HTMLButtonElement);
  const miniStrip = find(dialog, 'theme-minis', HTMLElement);
  const headerOptions = findAll(dialog, 'theme-header-option', HTMLButtonElement);
  const companyPicker = find(dialog, 'theme-companies', HTMLElement);
  const companies = findAll(dialog, 'theme-company', HTMLButtonElement);
  const previous = find(dialog, 'theme-previous', HTMLButtonElement);
  const next = find(dialog, 'theme-next', HTMLButtonElement);
  const position = find(dialog, 'theme-position', HTMLElement);
  const caption = find(dialog, 'theme-caption', HTMLElement);
  const groupName = find(dialog, 'theme-group', HTMLElement);
  const title = find(dialog, 'theme-name', HTMLElement);
  const text = find(dialog, 'theme-text', HTMLElement);
  const look = find(dialog, 'theme-look', HTMLElement);
  const motifs = find(dialog, 'theme-motifs', HTMLElement);
  const captions = find(motifs, 'theme-captions', HTMLElement);
  const quantity = find(dialog, 'quantity', HTMLInputElement);
  const year = data(dialog, 'year');
  const monthNames = slides.map(slide => data(slide, 'month'));

  let theme: Theme | null = null;
  let month = 0;
  let withHeader = false;
  let headers: HTMLImageElement[] = [];

  const renderMonth = (): void => {
    position.textContent = `${monthNames[month] ?? ''} ${year} · ${month + 1}/${slides.length}`;
    caption.textContent = theme?.captions[month] ?? '';
    previous.disabled = month === 0;
    next.disabled = month === slides.length - 1;
    minis.forEach((mini, index) => mini.setAttribute('aria-current', String(index === month)));
    const active = minis[month];
    if (active) miniStrip.scrollTo({ left: active.offsetLeft - (miniStrip.clientWidth - active.offsetWidth) / 2 });
  };

  const goTo = (index: number): void => {
    month = Math.max(0, Math.min(slides.length - 1, index));
    track.scrollTo({ left: month * track.clientWidth });
    renderMonth();
  };

  const setHeader = (on: boolean): void => {
    withHeader = on;
    dialog.classList.toggle('theme-detail--with-header', on);
    companyPicker.hidden = !on;
    headerOptions.forEach(option => option.setAttribute('aria-pressed', String(data(option, 'header') === String(on))));
  };

  const setCompany = (company: string): void => {
    companies.forEach(button => button.setAttribute('aria-pressed', String(data(button, 'company') === company)));
    headers.forEach(header => {
      header.src = headerImage(company);
    });
  };

  const setQuantity = (value: number): void => {
    const count = Number.isFinite(value) ? Math.round(value) : Number(quantity.min);
    quantity.value = String(Math.max(Number(quantity.min), Math.min(Number(quantity.max), count)));
  };

  const fill = (shown: Theme): void => {
    headers = pages.map((page, index) => {
      const header = image('theme-detail__header', headerImage(shown.company), '', 960, 496, true);
      const motif = shown.captions[index] ?? '';
      const alternative = `${shown.name}, ${monthNames[index] ?? ''} ${year}${motif === '' ? '' : `, ${motif}`}`;
      page.replaceChildren(
        header,
        image('theme-detail__sheet', sheetImage(shown.id, index), alternative, 760, 1068, index > 1)
      );
      return header;
    });
    const named = shown.captions.filter(motif => motif !== '');
    groupName.textContent = shown.groupName;
    title.textContent = shown.name;
    text.textContent = shown.text;
    look.textContent = shown.look;
    captions.textContent = named.join(' · ');
    motifs.hidden = named.length === 0;
    dialog.style.setProperty('--mini-sprite', `url("${miniSprite(shown.id)}")`);
  };

  const open = (trigger: HTMLElement, startWithHeader: boolean): void => {
    theme = themeFrom(trigger);
    fill(theme);
    setHeader(startWithHeader);
    setCompany(theme.company);
    setQuantity(Number(quantity.defaultValue));
    month = 0;
    dialog.showModal();
    track.scrollTo({ left: 0, behavior: 'instant' });
    renderMonth();
  };

  // Mesec koji je posle prevlačenja većim delom u traci postaje izabran.
  const observer = new IntersectionObserver(
    entries => {
      const visible = entries.find(entry => entry.isIntersecting);
      const index = slides.findIndex(slide => slide === visible?.target);
      if (index === -1 || index === month) return;
      month = index;
      renderMonth();
    },
    { root: track, threshold: 0.6 }
  );
  slides.forEach(slide => observer.observe(slide));

  $(previous).on('click', () => goTo(month - 1));
  $(next).on('click', () => goTo(month + 1));
  minis.forEach((mini, index) => $(mini).on('click', () => goTo(index)));
  headerOptions.forEach(option => $(option).on('click', () => setHeader(data(option, 'header') === 'true')));
  companies.forEach(button => $(button).on('click', () => setCompany(data(button, 'company'))));

  findAll(dialog, 'quantity-step', HTMLButtonElement).forEach(button =>
    $(button).on('click', () => {
      const current = quantity.valueAsNumber;
      const step = current >= largeStepFrom ? largeStep : 1;
      setQuantity(current + Number(data(button, 'step')) * step);
    })
  );
  findAll(dialog, 'quantity-preset', HTMLButtonElement).forEach(button =>
    $(button).on('click', () => setQuantity(Number(data(button, 'value'))))
  );
  $(quantity).on('change', () => setQuantity(quantity.valueAsNumber));

  $(find(dialog, 'theme-add', HTMLButtonElement)).on('click', () => {
    if (theme === null) return;
    setQuantity(quantity.valueAsNumber);
    cart.add({ theme: theme.id, name: theme.name, withHeader, quantity: quantity.valueAsNumber });
  });

  $(find(dialog, 'theme-close', HTMLButtonElement)).on('click', () => dialog.close());
  $(dialog).on('click', event => {
    if (event.target === dialog) dialog.close();
  });
  $(dialog).on('keydown', event => {
    if (event.target instanceof HTMLInputElement) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(month + (event.key === 'ArrowRight' ? 1 : -1));
    }
  });

  return { open };
}

function themeFrom(trigger: HTMLElement): Theme {
  return {
    id: data(trigger, 'theme'),
    name: data(trigger, 'name'),
    groupName: data(trigger, 'groupName'),
    text: data(trigger, 'text'),
    look: data(trigger, 'look'),
    company: data(trigger, 'company'),
    captions: data(trigger, 'captions').split('|'),
  };
}

function image(
  className: string,
  source: string,
  alternative: string,
  width: number,
  height: number,
  lazy: boolean
): HTMLImageElement {
  const element = new Image(width, height);
  element.className = className;
  element.alt = alternative;
  element.decoding = 'async';
  element.loading = lazy ? 'lazy' : 'eager';
  element.src = source;
  return element;
}
